<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import { format } from 'date-fns';

const props = defineProps({
  automation: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close', 'schedule']);

const { t } = useI18n();

const disableDateTime = ref('');
const isScheduling = ref(false);

const currentDisabledAt = computed(() => {
  if (props.automation.disabled_at) {
    return new Date(props.automation.disabled_at * 1000);
  }
  return null;
});

const hasScheduledDisable = computed(() => {
  return currentDisabledAt.value && currentDisabledAt.value > new Date();
});

const formattedCurrentSchedule = computed(() => {
  if (currentDisabledAt.value) {
    return format(currentDisabledAt.value, 'PPpp');
  }
  return null;
});

// Initialize with current schedule if exists
watch(
  () => props.automation.disabled_at,
  disabledAt => {
    if (disabledAt) {
      const date = new Date(disabledAt * 1000);
      disableDateTime.value = format(date, "yyyy-MM-dd'T'HH:mm");
    }
  },
  { immediate: true }
);

const minDateTime = computed(() => {
  const now = new Date();
  return format(now, "yyyy-MM-dd'T'HH:mm");
});

const handleSchedule = () => {
  if (!disableDateTime.value) {
    useAlert(t('AUTOMATION.SCHEDULE.SELECT_DATE_TIME'));
    return;
  }

  const selectedDateTime = new Date(disableDateTime.value);

  if (selectedDateTime <= new Date()) {
    useAlert(t('AUTOMATION.SCHEDULE.FUTURE_DATE_REQUIRED'));
    return;
  }

  isScheduling.value = true;
  emit('schedule', {
    id: props.automation.id,
    disabled_at: selectedDateTime.toISOString(),
  });
};

const handleRemoveSchedule = () => {
  isScheduling.value = true;
  emit('schedule', {
    id: props.automation.id,
    disabled_at: null,
  });
};

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <div>
    <woot-modal-header
      :header-title="$t('AUTOMATION.SCHEDULE.TITLE')"
      :header-content="$t('AUTOMATION.SCHEDULE.DESC')"
    />
    <div class="flex flex-col modal-content">
      <div class="w-full">
        <div
          v-if="hasScheduledDisable"
          class="mb-4 p-4 rounded-lg bg-n-yellow-2 dark:bg-n-yellow-10"
        >
          <p class="text-sm text-n-yellow-10 dark:text-n-yellow-2 mb-2">
            {{ $t('AUTOMATION.SCHEDULE.CURRENT_SCHEDULE') }}
          </p>
          <p
            class="text-base font-medium text-n-yellow-10 dark:text-n-yellow-2"
          >
            {{ formattedCurrentSchedule }}
          </p>
        </div>

        <div class="mb-6">
          <label class="block text-sm font-medium mb-2">
            {{ $t('AUTOMATION.SCHEDULE.DISABLE_AT') }}
          </label>
          <input
            v-model="disableDateTime"
            type="datetime-local"
            :min="minDateTime"
            class="w-full px-3 py-2 border border-n-weak rounded-md focus:outline-none focus:ring-2 focus:ring-n-blue-5"
          />
        </div>

        <div class="flex flex-row justify-end gap-2 px-0 py-2">
          <Button
            v-if="hasScheduledDisable"
            slate
            md
            :is-loading="isScheduling"
            @click="handleRemoveSchedule"
          >
            {{ $t('AUTOMATION.SCHEDULE.REMOVE') }}
          </Button>
          <Button slate md @click="handleClose">
            {{ $t('AUTOMATION.SCHEDULE.CANCEL') }}
          </Button>
          <Button blue md :is-loading="isScheduling" @click="handleSchedule">
            {{ $t('AUTOMATION.SCHEDULE.SAVE') }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
